import { createSlice } from '@reduxjs/toolkit';
import { APIRequestV2, cancelAPIRequest } from 'src/helpers/APIRequest';

const INITIAL_STATE = {
  listOfAccount: null,
  selectedAccount: '',
  cashPositionReportData: null,
  checkNumberSearchValue: '',
  showReconDetails: false,
  showCompact: false,
  page: 0,
  limit: 25,
  loading: false,
  error: false,
  errorMessage: '',
  brch: {
    required: false,
    ixBrch: 0,
    label: ''
  },
  formFilterData: {
    ixAcc: '',
    ixMode: 0,
    dt1: null,
    dt2: null,
    textValue: ''
  }
};

const slice = createSlice({
  name: 'cashPosition',
  initialState: INITIAL_STATE,
  reducers: {
    onFetchAccountList(state, action) {
      state.error = false;
      state.errorMessage = '';
    },
    loadAccounts(state, action) {
      state.loading = false;
      state.listOfAccount = action.payload.lstAcc;
    },
    loadError(state, action) {
      state.loading = false;
      state.errorMessage = action.payload.message;
      state.error = true;
    },
    selectAccount(state, action) {
      state.selectedAccount = action.payload;
      state.formFilterData.ixAcc = action.payload;
    },
    selectMode(state, action) {
      state.formFilterData.ixMode = action.payload;
    },
    resetReport(state, action) {
      return { ...INITIAL_STATE, listOfAccount: state.listOfAccount };
    },
    setDateRange(state, action) {
      state.formFilterData.dt1 = action.payload.dt1;
      state.formFilterData.dt2 = action.payload.dt2;
      state.formFilterData.textValue = action.payload.textValue;
    },
    clearData(state, action) {
      state.cashPositionReportData = null;
    },
    fetchReport(state, action) {
      state.checkNumberSearchValue = '';
      state.loading = true;
    },
    loadData(state, action) {
      state.cashPositionReportData = action.payload;
      state.loading = false;
    },
    clearError(state, action) {
      state.error = false;
      state.errorMessage = '';
    },
    setCheckNumber(state, action) {
      state.checkNumberSearchValue = action.payload;
      state.page = 0;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setBrch(state, action) {
      state.brch.label = action.payload.label;
      state.brch.required = action.payload.required;
    },
    setShowReconDetails(state, action) {
      state.showReconDetails = action.payload;
    },
    setShowCompact(state, action) {
      state.showCompact = action.payload;
    },
    changeBrch(state, action) {
      state.brch.ixBrch = action.payload;
    },
    cancelRequest(state, action) {
      state.loading = false;
    },
    restore(_, action) {
      return action.payload || INITIAL_STATE;
    },
    clear(state, action) {
      return INITIAL_STATE;
    }
  }
});

export const fetchAccounts = () => async (dispatch, getState) => {
  const { base_url, userToken } = getState().auth;
  const { onFetchAccountList, loadAccounts, loadError } = slice.actions;
  dispatch(onFetchAccountList());

  const { success, data } = await APIRequestV2({
    url: `${base_url}/reports/cashpo/init`,
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      'x-access-tokens': userToken
    }
  });

  if (!success) {
    loadError({
      message: `Error - Something went wrong.`
    });
    return;
  }

  dispatch(loadAccounts(data));
};

export const getReport = () => async (dispatch, getState) => {
  const { formFilterData, brch } = getState().cashPosition;
  const { base_url, userToken } = getState().auth;
  const {
    clearData,
    clearError,
    fetchReport,
    loadError,
    loadData
  } = slice.actions;

  if (formFilterData.ixAcc === '' || formFilterData.ixAcc === null) {
    dispatch(loadError({ message: 'Please select an account.' }));
    return;
  }

  if (brch.required && brch.ixBrch === '') {
    dispatch(loadError({ message: `${brch.label} is required.` }));
    return;
  }

  if (formFilterData.ixMode === '' || formFilterData.ixMode === null) {
    dispatch(loadError({ message: 'Please select mode.' }));
    return;
  }

  if (formFilterData.dt1 === null || formFilterData.dt2 === null) {
    dispatch(
      loadError({
        message: 'Start date or End date is empthy.'
      })
    );
    return;
  }

  const dt1 = new Date(formFilterData.dt1).getTime();
  const dt2 = new Date(formFilterData.dt2).getTime();

  if (dt1 > dt2) {
    dispatch(
      loadError({
        message: 'Start date cannot be greater than end date'
      })
    );
    return;
  }

  dispatch(clearData());
  dispatch(clearError());
  dispatch(fetchReport());

  const payload = { ...formFilterData };

  if (brch.required) payload.ixBrch = brch.ixBrch;

  const { success, data, isCancelled } = await APIRequestV2({
    method: 'POST',
    url: `${base_url}/reports/cashpo`,
    headers: {
      'Content-Type': 'application/json',
      'x-access-tokens': userToken
    },
    data: payload
  });

  if (!success && isCancelled) return;
  else if (!success)
    dispatch(
      loadError({
        message: 'Error - Something went wrong'
      })
    );
  else dispatch(loadData(data));
};

export const cancelRequest = () => (dispatch, getState) => {
  const { cancelRequest } = slice.actions;
  if (cancelAPIRequest) cancelAPIRequest();
  dispatch(cancelRequest());
};

const cashPositionReducer = slice.reducer;
export const {
  fetchAccountList,
  loadAccounts,
  loadError,
  selectAccount,
  selectMode,
  resetReport,
  setDateRange,
  clearData,
  fetchReport,
  clearError,
  setCheckNumber,
  setLimit,
  setPage,
  setBrch,
  changeBrch,
  restore,
  clear,
  setShowReconDetails,
  loadData,
  setShowCompact
} = slice.actions;
export default cashPositionReducer;
